import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

// Field-level validators. Each returns an error string, or '' when valid.
const validators = {
  name: (value) => {
    if (!value.trim()) return 'Please enter your name.';
    if (value.trim().length < 2) return 'Name looks too short.';
    return '';
  },
  email: (value) => {
    if (!value.trim()) return 'Please enter your email.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      return 'Enter a valid email address, like name@company.com.';
    }
    return '';
  },
  company: () => '',
  message: (value) => {
    if (!value.trim()) return 'Please add a short message so we know how to help.';
    if (value.trim().length < 10) return 'A few more details would help us respond well.';
    return '';
  }
};

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Re-validate live, but only once the field has been touched — so we
    // don't flash a red error before the person has had a chance to type.
    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: validators[name](value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validators[name](value) }));
  };

  const validateAll = () => {
    const nextErrors = {};
    Object.keys(validators).forEach((field) => {
      nextErrors[field] = validators[field](formData[field]);
    });
    setErrors(nextErrors);
    setTouched({ name: true, email: true, company: true, message: true });
    return Object.values(nextErrors).every((msg) => !msg);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAll()) {
      setSubmitStatus({
        type: 'error',
        message: 'Please fix the highlighted fields before sending.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          access_key: '05da9f4d-1992-4f1e-b5ed-1caeba32b21a',
          subject: `New contact form submission from ${formData.name}`,
          from_name: 'QuByt AI Website',
          ...formData,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for your message! We\'ll get back to you within 24 hours.'
        });
        setFormData({ name: '', email: '', company: '', message: '' });
        setTouched({});
        setErrors({});
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="dark-full-container" style={{ 
      paddingTop: '160px', 
      paddingBottom: '160px',
      backgroundColor: 'var(--bg-secondary)',
      borderTop: '1px solid var(--border-subtle)'
    }}>
      <div className="dark-content-container">
        <div style={{ textAlign: 'center', marginBottom: '100px' }}>
          <h2 className="display-large" style={{ marginBottom: '24px', color: 'var(--brand-primary)' }}>
            Get in Touch
          </h2>
          <p className="body-large" style={{ maxWidth: '800px', margin: '0 auto' }}>
            Ready to transform your business ideas into production-ready applications? Let's talk about how QuByt AI can help you build faster, safer, and smarter.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {/* Contact Information */}
          <div style={{ minWidth: 0 }}>
            <h3 className="heading-1" style={{ marginBottom: '32px' }}>
              Contact Information
            </h3>
            <p className="body-medium" style={{ marginBottom: '40px' }}>
              Have questions about our platform? Want to see a demo? Our team is here to help you understand how QuByt AI can accelerate your development process.
            </p>

            <div style={{ marginBottom: '32px' }}>
              <div className="info-icon-row" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px'
              }}>
                <Mail size={24} className="info-icon" color="var(--brand-primary)" />
                <div>
                  <div className="body-medium" style={{ fontWeight: 500 }}>
                    Email
                  </div>
                  <div className="body-medium" style={{ color: 'var(--text-muted)' }}>
                    info@qubyt.ai
                  </div>
                </div>
              </div>

              <div className="info-icon-row" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px'
              }}>
                <MapPin size={24} className="info-icon" color="var(--brand-primary)" />
                <div>
                  <div className="body-medium" style={{ fontWeight: 500 }}>
                    Headquarters
                  </div>
                  <div className="body-medium" style={{ color: 'var(--text-muted)' }}>
                    Atlanta, GA, USA
                  </div>
                </div>
              </div>

              <div className="info-icon-row" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px'
              }}>
                <Phone size={24} className="info-icon" color="var(--brand-primary)" />
                <div>
                  <div className="body-medium" style={{ fontWeight: 500 }}>
                    Support
                  </div>
                  <div className="body-medium" style={{ color: 'var(--text-muted)' }}>
                    Available 24/7 via email
                  </div>
                </div>
              </div>

              <div className="info-icon-row" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <MapPin size={24} className="info-icon" color="var(--brand-primary)" />
                <div>
                  <div className="body-medium" style={{ fontWeight: 500 }}>
                    Response Time
                  </div>
                  <div className="body-medium" style={{ color: 'var(--text-muted)' }}>
                    Within 24 hours
                  </div>
                </div>
              </div>
            </div>

            <div className="tilt-panel" style={{
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-subtle)',
              padding: '30px',
              borderRadius: '6px'
            }}>
              <h4 className="heading-3" style={{ marginBottom: '16px' }}>
                Enterprise Inquiries
              </h4>
              <p className="body-small" style={{ marginBottom: '16px' }}>
                For enterprise partnerships, custom solutions, or volume pricing, our team can provide personalized consultation and tailored deployment strategies.
              </p>
              <p className="body-small" style={{ color: 'var(--brand-primary)' }}>
                Mention "Enterprise" in your message for priority handling.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div style={{ minWidth: 0 }}>
            <form onSubmit={handleSubmit} className="tilt-panel" style={{
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-subtle)',
              padding: '40px',
              borderRadius: '6px'
            }}>
              <h3 className="heading-1" style={{ marginBottom: '32px' }}>
                Send us a Message
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <label className="body-medium" style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  fontWeight: 500 
                }}>
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  aria-invalid={Boolean(touched.name && errors.name)}
                  aria-describedby={touched.name && errors.name ? 'name-error' : undefined}
                  className={`form-input${touched.name && errors.name ? ' form-input-error' : ''}`}
                  placeholder="Your full name"
                />
                {touched.name && errors.name && (
                  <div id="name-error" className="form-error" role="alert">
                    <AlertCircle size={14} />
                    {errors.name}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label className="body-medium" style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  fontWeight: 500 
                }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  aria-invalid={Boolean(touched.email && errors.email)}
                  aria-describedby={touched.email && errors.email ? 'email-error' : undefined}
                  className={`form-input${touched.email && errors.email ? ' form-input-error' : ''}`}
                  placeholder="your.email@company.com"
                />
                {touched.email && errors.email && (
                  <div id="email-error" className="form-error" role="alert">
                    <AlertCircle size={14} />
                    {errors.email}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label className="body-medium" style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  fontWeight: 500 
                }}>
                  Company
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  className="form-input"
                  placeholder="Your company name"
                />
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label className="body-medium" style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  fontWeight: 500 
                }}>
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  rows={6}
                  aria-invalid={Boolean(touched.message && errors.message)}
                  aria-describedby={touched.message && errors.message ? 'message-error' : undefined}
                  className={`form-input${touched.message && errors.message ? ' form-input-error' : ''}`}
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="Tell us about your project, questions, or how we can help..."
                />
                {touched.message && errors.message && (
                  <div id="message-error" className="form-error" role="alert">
                    <AlertCircle size={14} />
                    {errors.message}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary"
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: isSubmitting ? 0.7 : 1
                }}
              >
                {isSubmitting ? (
                  <Loader2 size={20} className="spin-icon" />
                ) : (
                  <Send size={20} />
                )}
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus && (
                <div
                  className="submit-status-in"
                  style={{
                    marginTop: '20px',
                    padding: '16px',
                    borderRadius: '6px',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    background: submitStatus.type === 'success' 
                      ? 'rgba(var(--brand-primary-rgb), 0.1)' 
                      : 'rgba(255, 0, 0, 0.1)',
                    border: `1px solid ${submitStatus.type === 'success' 
                      ? 'var(--brand-primary)' 
                      : '#ff0000'}`,
                    color: submitStatus.type === 'success' 
                      ? 'var(--brand-primary)' 
                      : '#ff0000'
                  }}
                >
                  {submitStatus.type === 'success' && <CheckCircle2 size={18} />}
                  {submitStatus.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;